/*
 * Copyright (c) 2026, Wultra s.r.o. (www.wultra.com).
 *
 * All rights reserved. This source code can be used only for purposes specified
 * by the given license contract signed by the rightful deputy of Wultra s.r.o.
 * This source code can be used only by the owner of the license.
 *
 * Any disputes arising in respect of this agreement (license) shall be brought
 * before the Municipal Court of Prague.
 */

import { observer } from "mobx-react-lite"

import { Card, CardContent, CardHeader, CardTitle } from "../primitives/card"

interface Props {
    /**
     * Permissions declared by the APK, in API order.
     */
    permissions: string[]
}

/**
 * Lists the permissions declared by the APK.
 *
 * Permission names use a monospace font and can wrap anywhere because custom
 * permissions may contain long package names. An APK with no permissions shows
 * an explicit empty state instead.
 *
 * @example
 * <ApkPermissions permissions={apk.permissions} />
 */
export const ApkPermissions = observer(({ permissions }: Props) => (
    <Card>
        <CardHeader>
            <CardTitle>
                <h3 className="m-0 text-xl font-bold uppercase">Permissions</h3>
            </CardTitle>
        </CardHeader>
        <CardContent>
            {permissions.length === 0 ? (
                <p className="m-0 text-muted-foreground">The APK declares no permissions.</p>
            ) : (
                // Preserve list semantics in Safari when the list has no default styling.
                <ul role="list" className="m-0 grid list-none gap-1 p-0 font-mono text-xs wrap-anywhere">
                    {permissions.map((permission) => (
                        <li key={permission}>{permission}</li>
                    ))}
                </ul>
            )}
        </CardContent>
    </Card>
))
